// Pure helpers for the Fixed costs page (Fan, 2026-10-29: "make the overview,
// subscriptions and other fixed costs tabs more like the Taxes tab" and "make
// the chart bar split subscriptions vs others"). Input is the
// /subscriptions/by-jurisdiction payload (all recurring types); every figure is
// already in the display currency.

export const TYPE_SERIES = [
  { key: 'subscription', label: 'Subscriptions', color: '#6366F1' },
  { key: 'rent', label: 'Other fixed costs', color: '#F59E0B' },
];

const ENTITY_COLORS = ['#6366F1', '#F59E0B', '#10B981', '#EF4444', '#0EA5E9', '#A855F7', '#9CA3AF'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function monthLabel(ym) {
  const [y, m] = String(ym).split('-');
  const name = MONTHS[Number(m) - 1];
  return name ? `${name} ${String(y).slice(2)}` : String(ym);
}

export function flattenRows(data) {
  const rows = [];
  (data?.jurisdictions || []).forEach((j) => {
    (j.entities || []).forEach((e) => {
      (e.vendors || []).forEach((v) => {
        rows.push({ ...v, entity_id: e.entity_id, entity_name: e.entity_name, jurisdiction: j.jurisdiction_label });
      });
    });
  });
  return rows;
}

function monthKeys(rows) {
  const keys = new Set();
  rows.forEach((r) => Object.keys(r.month_totals || {}).forEach((k) => keys.add(k)));
  return [...keys].sort();
}

// Month bars split Subscriptions vs Other fixed costs (Needs review left out,
// like the totals).
export function typeMonthChart(rows) {
  const counted = rows.filter((r) => r.spend_type === 'subscription' || r.spend_type === 'rent');
  return monthKeys(counted).map((ym) => {
    const point = { label: monthLabel(ym), subscription: 0, rent: 0 };
    counted.forEach((r) => {
      point[r.spend_type] += Number((r.month_totals || {})[ym] || 0);
    });
    return point;
  });
}

// Month bars for one type, split by company/person (largest 5, rest grouped).
export function entityMonthChart(rows, spendType) {
  const typed = rows.filter((r) => r.spend_type === spendType);
  const byEntity = {};
  typed.forEach((r) => {
    const name = r.entity_name || 'Unassigned';
    byEntity[name] = (byEntity[name] || 0) + Number(r.monthly || 0);
  });
  const ranked = Object.keys(byEntity).sort((a, b) => byEntity[b] - byEntity[a]);
  const top = ranked.slice(0, 5);
  const rest = ranked.length > 5;
  const series = top.map((name, i) => ({ key: `e${i}`, label: name, color: ENTITY_COLORS[i] }));
  if (rest) series.push({ key: 'eRest', label: 'Others', color: ENTITY_COLORS[6] });
  const keyOf = (name) => {
    const i = top.indexOf(name);
    return i >= 0 ? `e${i}` : 'eRest';
  };
  const data = monthKeys(typed).map((ym) => {
    const point = { label: monthLabel(ym) };
    series.forEach((s) => { point[s.key] = 0; });
    typed.forEach((r) => {
      point[keyOf(r.entity_name || 'Unassigned')] += Number((r.month_totals || {})[ym] || 0);
    });
    return point;
  });
  return { data, series };
}

// One row per company/person: subscriptions, other fixed costs, total and
// the payees behind them, biggest first.
export function entityTable(rows, spendTypes = ['subscription', 'rent']) {
  const map = {};
  rows.filter((r) => spendTypes.includes(r.spend_type)).forEach((r) => {
    const key = r.entity_id || 'unassigned';
    const e = map[key] || (map[key] = {
      key, name: r.entity_name || 'Unassigned', jurisdiction: r.jurisdiction,
      subscription: 0, rent: 0, total: 0, vendors: [],
    });
    const amt = r.unconvertible ? 0 : Number(r.monthly || 0);
    e[r.spend_type] += amt;
    e.total += amt;
    e.vendors.push(r);
  });
  const list = Object.values(map);
  list.forEach((e) => e.vendors.sort((a, b) => Number(b.monthly || 0) - Number(a.monthly || 0)));
  return list.sort((a, b) => b.total - a.total);
}

export function typeStats(rows, spendType) {
  const typed = rows.filter((r) => r.spend_type === spendType)
    .sort((a, b) => Number(b.monthly || 0) - Number(a.monthly || 0));
  const total = typed.reduce((s, r) => s + (r.unconvertible ? 0 : Number(r.monthly || 0)), 0);
  const months = monthKeys(typed);
  const last = months[months.length - 1];
  const lastMonth = last
    ? typed.reduce((s, r) => s + Number((r.month_totals || {})[last] || 0), 0)
    : null;
  return { count: typed.length, total, biggest: typed[0] || null, lastMonthKey: last, lastMonth };
}
